import { useMsal } from "@azure/msal-react";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Alert, Button, Modal } from "react-bootstrap";
import { Invite } from "../../models/invite.model";
import { msalConfig } from "../../config/msalConfig";
import { createAxiosInstance } from "../../utils/axios-instance";
import { USER_AUTH_TYPE } from "../../enums/user-auth-type.enum";
import { ProgramType } from "../../models/program-type.model";
import styles from "../../scss/TermsAndConditions.module.scss";
import { formatDateWithTimezone } from "../../utils/get-date";
import { getLatestTerms } from "../../services/terms-and-conditions.service";
import { sanitizeTermsHtml } from "../../utils/sanitize-terms-html";
import { createMsalInstance } from "../../utils/create-msal-instance";
type TermsAndConditionsModalProps = {
  invite: Partial<Invite> | null;
  show: boolean;
  handleClose: () => void;
  confirmTermsAndConditions: () => void;
  programType: ProgramType;
};
export default function TermsAndConditionsModal({
  show,
  handleClose,
  invite,
  confirmTermsAndConditions,
  programType,
}: TermsAndConditionsModalProps) {
  const { accounts } = useMsal();
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isTermsLoading, setIsTermsLoading] = useState(false);
  const [termsLoadError, setTermsLoadError] = useState("");
  const [termsHtml, setTermsHtml] = useState("");
  const modalBodyRef = useRef<HTMLDivElement>(null);

  const loadLatestTerms = useCallback(async () => {
    if (!show) {
      return;
    }

    if (programType !== "PB" && programType !== "PRP") {
      setTermsLoadError("Unsupported program code for terms and conditions.");
      return;
    }

    setHasScrolledToBottom(false);
    setIsTermsLoading(true);
    setTermsLoadError("");
    setTermsHtml("");

    try {
      const authority = `${process.env.REACT_APP_MSAL_AUTHORITY}/${USER_AUTH_TYPE.COMARCH_CUSTOMER}`;
      msalConfig.auth.authority = authority;
      msalConfig.auth.redirectUri =
        process.env.REACT_APP_MSAL_REGISTRATION_REDIRECT_URI || "";
      const msalInstance = await createMsalInstance();

      const latestTerms = await getLatestTerms(programType, msalInstance);
      const sanitizedHtml = sanitizeTermsHtml(latestTerms.contentHtml);
      if (!sanitizedHtml.trim()) {
        throw new Error("Terms and conditions content is empty.");
      }
      setTermsHtml(sanitizedHtml);
    } catch (error) {
      setTermsLoadError(
        "Unable to load the latest terms and conditions. Please try again."
      );
    } finally {
      setIsTermsLoading(false);
    }
  }, [programType, show]);

  const acknowledge = async () => {
    if (isTermsLoading || termsLoadError || !termsHtml) {
      return;
    }

    setIsProcessing(true);
    setHasError(false);
    const payload = {
      invitationKey: invite?.invitationKey || "",
      loyaltyId: `${programType}-${invite?.ownerID}`,
      ownerFirstName: invite?.ownerFirstName,
      ownerLastName: invite?.ownerLastName,
      enrolled: 1,
      termsAndConditionsAccepted: 1,
      enrolledDate: formatDateWithTimezone(new Date()),
      acceptedDateDate: formatDateWithTimezone(new Date()),
      ownerAzureId: accounts[0].idTokenClaims?.sub,
    };
    //console.log('@@ - ENROLLMENT payload => ', JSON.stringify(payload));
    try {
      const authority = `${process.env.REACT_APP_MSAL_AUTHORITY}/${USER_AUTH_TYPE.COMARCH_CUSTOMER}`;
      msalConfig.auth.authority = authority;
      const msalInstance = await createMsalInstance();
      const axiosInstance = createAxiosInstance(msalInstance);
      await axiosInstance.patch(
        `${process.env.REACT_APP_INVITE_API_URL}/rewards/enrollment`,
        payload
      );

      confirmTermsAndConditions();
    } catch (err) {
      console.error("Error acknowledging terms and conditions:", err);
      setErrorMessage((err as any)?.response?.data?.message || "Unknown error");
      setIsProcessing(false);
      setHasError(true);

      setTimeout(() => {
        if (modalBodyRef.current) {
          modalBodyRef.current.scrollTo({
            top: modalBodyRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  };

  const handleScroll = (e: any) => {
    if (isTermsLoading || !!termsLoadError || !termsHtml) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const scrollPercent = (scrollTop / (scrollHeight - clientHeight)) * 100;

    // Consider scrolled to bottom if user has scrolled at least 95%
    if (scrollPercent >= 95 && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const getDisplayErrorMessage = (message: string, programType: string) => {
    if (message === "Internal Server Error for Batch: ERROR: This Loyalty Program already exists") {
      const email = programType === "PRP" ? "PRsupport@essilorusa.com" : "PBsupport@essilorusa.com";
      return (
        <>
          There was an issue completing your enrollment. Please reach out to customer service for assistance. &nbsp;
          <a href={`mailto:${email}`} className="text-blue-600 underline">
            {email}
          </a>
        </>
      );
    }
    return message;
  };

  // Reset states when modal is closed
  useEffect(() => {
    if (show) {
      loadLatestTerms();
      return;
    }

    setIsProcessing(false);
    setHasScrolledToBottom(false);
    setHasError(false);
    setErrorMessage("");
    setIsTermsLoading(false);
    setTermsLoadError("");
    setTermsHtml("");
  }, [loadLatestTerms, show]);

  return (
    <Modal
      className={styles.termsWrapper}
      size="lg"
      show={show}
      onHide={handleClose}
    >
      <Modal.Header closeButton>
        <Modal.Title>Terms and Conditions</Modal.Title>
      </Modal.Header>
      <Modal.Body
        ref={modalBodyRef}
        onScroll={handleScroll}
        className="h-[400px] overflow-y-scroll"
      >
        {isTermsLoading && (
          <p className="text-center m-0">Loading latest terms and conditions...</p>
        )}
        {!isTermsLoading && !!termsLoadError && (
          <Alert className="text-center mx-auto mt-4" variant="danger">
            <p className="mb-3">{termsLoadError}</p>
            <Button
              variant="outline-danger"
              onClick={loadLatestTerms}
              disabled={isProcessing}
            >
              Retry
            </Button>
          </Alert>
        )}
        {!isTermsLoading && !termsLoadError && !!termsHtml && (
          <div dangerouslySetInnerHTML={{ __html: termsHtml }} />
        )}
        {hasError && (
          <Alert className="text-center mx-auto mt-4" variant="danger">
            {getDisplayErrorMessage(errorMessage, programType)}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-between w-full">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            id="submitButton"
            disabled={
              isProcessing ||
              isTermsLoading ||
              !!termsLoadError ||
              !termsHtml ||
              !hasScrolledToBottom ||
              !!errorMessage
            }
            variant="primary"
            onClick={acknowledge}
          >
            {isProcessing ? "Processing..." : "Confirm & Accept"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
